import { useEffect, useRef, useState } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { ChevronLeft, ChevronRight, Download, ImageOff, X } from "lucide-react";
import { cn, getUploadUrl } from "@/lib/utils";

/**
 * Trình xem nhiều ảnh (04/11/2026): luôn ở giữa MÀN HÌNH thiết bị, kể cả khi mở từ trong popup đã cuộn.
 * Là hộp thoại Radix riêng (portal ra body) — lớp `fixed inset-0` cũ nằm trong DialogContent có transform nên bám theo
 * nội dung popup, phải cuộn lên mới thấy ảnh. Esc / bấm nền / ✕ chỉ đóng ảnh, popup cha vẫn mở.
 * Nhiều ảnh: ‹ ›, phím ← →, vuốt ngang; bộ đếm i/n. Một ảnh có nút mở thì dùng ImageLightbox (cùng thư mục).
 */
export function ImageGalleryLightbox({
  images,
  index,
  onIndexChange,
  onClose,
}: {
  images: string[];
  index: number | null;
  onIndexChange: (index: number) => void;
  onClose: () => void;
}) {
  const open = index !== null && images.length > 0;
  const current = open ? Math.min(Math.max(index, 0), images.length - 1) : 0;
  const many = images.length > 1;
  const src = open ? getUploadUrl(images[current]) : "";
  const [failed, setFailed] = useState(false);
  const swipeX = useRef<number | null>(null);

  useEffect(() => setFailed(false), [src]);

  const go = (step: number) => {
    if (many) onIndexChange((current + step + images.length) % images.length);
  };

  return (
    <DialogPrimitive.Root open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-[200] bg-black/85 backdrop-blur-sm data-[state=open]:animate-in data-[state=open]:fade-in-0" />
        <DialogPrimitive.Content
          data-testid="image-lightbox"
          aria-describedby={undefined}
          className="fixed inset-0 z-[201] flex items-center justify-center p-4 outline-none select-none"
          onKeyDown={(e) => {
            if (e.key === "ArrowRight") go(1);
            if (e.key === "ArrowLeft") go(-1);
          }}
          onPointerDown={(e) => {
            swipeX.current = e.clientX;
          }}
          onPointerUp={(e) => {
            const start = swipeX.current;
            swipeX.current = null;
            if (start !== null && Math.abs(e.clientX - start) > 40) go(e.clientX < start ? 1 : -1);
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) onClose();
          }}
        >
          <DialogPrimitive.Title className="sr-only">Xem ảnh</DialogPrimitive.Title>
          {open &&
            (failed ? (
              <div className="flex flex-col items-center gap-2 rounded-lg bg-white/10 px-8 py-10 text-sm text-white/80">
                <ImageOff className="h-8 w-8" />
                Không tải được ảnh
              </div>
            ) : (
              <img
                data-testid="image-lightbox-image"
                src={src}
                alt={`Ảnh ${current + 1}`}
                draggable={false}
                onError={() => setFailed(true)}
                className="max-h-[86dvh] max-w-[92vw] rounded-md object-contain shadow-2xl"
              />
            ))}
          <div className="absolute right-4 top-4 flex items-center gap-2">
            {open && !failed && (
              <a href={src} target="_blank" rel="noreferrer" download aria-label="Tải ảnh về máy" title="Tải ảnh về máy" className={roundClass}>
                <Download className="h-4 w-4" />
              </a>
            )}
            <DialogPrimitive.Close aria-label="Đóng" title="Đóng (Esc)" className={roundClass}>
              <X className="h-5 w-5" />
            </DialogPrimitive.Close>
          </div>
          {many && (
            <>
              <button type="button" aria-label="Ảnh trước" data-testid="image-lightbox-prev" onClick={() => go(-1)} className={cn(roundClass, "absolute left-3 top-1/2 -translate-y-1/2")}>
                <ChevronLeft className="h-6 w-6" />
              </button>
              <button type="button" aria-label="Ảnh sau" data-testid="image-lightbox-next" onClick={() => go(1)} className={cn(roundClass, "absolute right-3 top-1/2 -translate-y-1/2")}>
                <ChevronRight className="h-6 w-6" />
              </button>
              <div
                data-testid="image-lightbox-counter"
                className="absolute bottom-[max(1rem,env(safe-area-inset-bottom))] left-1/2 -translate-x-1/2 rounded-full bg-black/60 px-3 py-1 text-sm font-medium tabular-nums text-white"
              >
                {current + 1}/{images.length}
              </div>
            </>
          )}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

const roundClass = "flex h-11 w-11 items-center justify-center rounded-full bg-white/15 text-white hover:bg-white/25 transition-colors";
